import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Bell, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

const statusLabels: Record<string, string> = {
  actie_luijten: "Actie Luijten",
  actie_decoprof: "Actie Decoprof",
  afwachting_voorraad: "Afwachting voorraad",
  afwachting_klant: "Afwachting klant",
  afwachting_voorraad_ridderkerk: "Voorraad Ridderkerk",
  actie_winkel_ridderkerk: "Winkel Ridderkerk",
  afwijkende_verzenddatum: "Afwijkende verzenddatum",
};

const DashboardNotificationStats = () => {
  const { data: stats } = useQuery({
    queryKey: ["dashboard-notification-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("status, priority")
        .neq("status", "afgerond");

      if (error) throw error;

      const total = data?.length || 0;
      const urgent = data?.filter((n) => n.priority === "hoog").length || 0;
      const byStatus: Record<string, number> = {};
      data?.forEach((n) => {
        byStatus[n.status] = (byStatus[n.status] || 0) + 1;
      });

      return { total, urgent, byStatus };
    },
    refetchInterval: 30000,
  });

  const { data: completedToday } = useQuery({
    queryKey: ["dashboard-completed-today"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const { count } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("status", "afgerond")
        .gte("updated_at", today.toISOString());
      return count || 0;
    },
    refetchInterval: 30000,
  });

  return (
    <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
      <Link to="/notifications">
        <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-primary">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Open meldingen
            </CardTitle>
            <Bell className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats?.total ?? "—"}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Actieve meldingen
            </p>
          </CardContent>
        </Card>
      </Link>

      <Link to="/notifications?priority=hoog">
        <Card className="hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-destructive">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Urgent
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">
              {stats?.urgent ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Hoge prioriteit
            </p>
          </CardContent>
        </Card>
      </Link>

      <Card className="border-l-4 border-l-amber-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Wachtend
          </CardTitle>
          <Clock className="h-4 w-4 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">
            {stats
              ? (stats.byStatus["afwachting_voorraad"] || 0) +
                (stats.byStatus["afwachting_klant"] || 0) +
                (stats.byStatus["afwachting_voorraad_ridderkerk"] || 0)
              : "—"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            In afwachting
          </p>
        </CardContent>
      </Card>

      <Card className="border-l-4 border-l-green-500">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Vandaag afgerond
          </CardTitle>
          <CheckCircle2 className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-3xl font-bold text-green-600">
            {completedToday ?? "—"}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Afgehandeld vandaag
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardNotificationStats;
